import React from 'react';
import { 
  CheckCircleIcon,
  FireIcon,
  MusicalNoteIcon,
  TrophyIcon
} from '@heroicons/react/24/solid';

/**
 * RequirementsBanner - Shows unlock requirements progress for subscribed users
 * who haven't yet met the requirements
 */
const RequirementsBanner = ({ accessData }) => {
  const { requirements, stats } = accessData;

  const formatExerciseName = (name) => {
    if (!name) return 'Unknown';
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-l-4 border-purple-500 rounded-lg p-4 mb-4">
      <div className="flex items-start gap-3 mb-3">
        <div className="flex-shrink-0">
          <div className="h-10 w-10 bg-purple-600 rounded-full flex items-center justify-center">
            <span className="text-white font-bold text-lg">!</span>
          </div>
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-bold text-gray-900 mb-1">
            Build Your Analytics Foundation
          </h3>
          <p className="text-sm text-gray-700 mb-3">
            Complete these requirements to unlock the full power of your analytics dashboard. Your practice data will become more meaningful as you record more sessions!
          </p>
        </div>
      </div>

      {/* Compact Requirements Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Requirement 1: Total Sessions */}
        <div className={`bg-white rounded-lg p-3 border-2 ${
          requirements.total_sessions.met ? 'border-green-500' : 'border-gray-200'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <MusicalNoteIcon className={`h-5 w-5 ${
                requirements.total_sessions.met ? 'text-green-500' : 'text-gray-400'
              }`} />
              <span className="font-semibold text-sm text-gray-900">1,000 Recordings</span>
            </div>
            {requirements.total_sessions.met && (
              <CheckCircleIcon className="h-5 w-5 text-green-500" />
            )}
          </div>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-gray-700">
              {requirements.total_sessions.current.toLocaleString()} / {requirements.total_sessions.required.toLocaleString()}
            </span>
            <span className={`font-semibold ${
              requirements.total_sessions.met ? 'text-green-600' : 'text-indigo-600'
            }`}>
              {requirements.total_sessions.met ? '✓' : `${requirements.total_sessions.remaining.toLocaleString()} to go`}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all duration-500 ${
                requirements.total_sessions.met 
                  ? 'bg-gradient-to-r from-green-500 to-green-600' 
                  : 'bg-gradient-to-r from-indigo-500 to-indigo-600'
              }`}
              style={{ 
                width: `${Math.min(100, (requirements.total_sessions.current / requirements.total_sessions.required) * 100)}%` 
              }}
            />
          </div>
        </div>

        {/* Requirement 2: Favorite Exercise */}
        <div className={`bg-white rounded-lg p-3 border-2 ${
          requirements.favorite_exercise.met ? 'border-green-500' : 'border-gray-200'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <TrophyIcon className={`h-5 w-5 ${
                requirements.favorite_exercise.met ? 'text-green-500' : 'text-gray-400'
              }`} />
              <span className="font-semibold text-sm text-gray-900">Top Exercise 5x</span>
            </div>
            {requirements.favorite_exercise.met && (
              <CheckCircleIcon className="h-5 w-5 text-green-500" />
            )}
          </div>
          {requirements.favorite_exercise.exercise_name && (
            <div className="text-xs text-purple-900 mb-1 truncate font-medium">
              {formatExerciseName(requirements.favorite_exercise.exercise_name)}
            </div>
          )}
          <div className="flex justify-between text-xs mb-1">
            <span className="text-gray-700">
              {requirements.favorite_exercise.current} / {requirements.favorite_exercise.required}
            </span>
            <span className={`font-semibold ${
              requirements.favorite_exercise.met ? 'text-green-600' : 'text-purple-600'
            }`}>
              {requirements.favorite_exercise.met ? '✓' : `${requirements.favorite_exercise.remaining} to go`}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all duration-500 ${
                requirements.favorite_exercise.met 
                  ? 'bg-gradient-to-r from-green-500 to-green-600' 
                  : 'bg-gradient-to-r from-purple-500 to-purple-600'
              }`}
              style={{ 
                width: `${Math.min(100, (requirements.favorite_exercise.current / requirements.favorite_exercise.required) * 100)}%` 
              }}
            />
          </div>
        </div>

        {/* Requirement 3: Practice Streak */}
        <div className={`bg-white rounded-lg p-3 border-2 ${
          requirements.streak.met ? 'border-green-500' : 'border-gray-200'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <FireIcon className={`h-5 w-5 ${
                requirements.streak.met ? 'text-green-500' : 'text-gray-400'
              }`} />
              <span className="font-semibold text-sm text-gray-900">3-Day Streak</span>
            </div>
            {requirements.streak.met && (
              <CheckCircleIcon className="h-5 w-5 text-green-500" />
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 mb-1">
            <div className="bg-orange-50 rounded p-1 text-center">
              <div className="text-base font-bold text-orange-900">
                {requirements.streak.current}
              </div>
              <div className="text-xs text-orange-700">Current</div>
            </div>
            <div className="bg-orange-50 rounded p-1 text-center">
              <div className="text-base font-bold text-orange-900">
                {requirements.streak.longest}
              </div>
              <div className="text-xs text-orange-700">Longest</div>
            </div>
          </div>
          {!requirements.streak.met && (
            <div className="text-xs text-gray-600 text-center">
              {requirements.streak.current > 0 
                ? `${3 - requirements.streak.current} more day${3 - requirements.streak.current === 1 ? '' : 's'}!`
                : 'Start your streak today!'
              }
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequirementsBanner;

